import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import i18next from 'i18next';
import { createPendingFileMutation } from '../../db/api/pending-file.js';
import { languageMap } from '../../i18n.js';
import { type SampleExamFile, ROLE_LABEL_KEYS } from './sample-exams.js';

export async function loadSampleExamFiles(files: SampleExamFile[], examId: string): Promise<string[]> {
  const pendingFileNames = await Promise.all(
    files.map(async sampleFile => {
      const response = await fetch(sampleFile.url);
      if (!response.ok) throw new Error(`Failed to fetch sample file: ${sampleFile.fileName}`);
      const blob = await response.blob();
      const file = new File([blob], sampleFile.fileName, { type: 'application/pdf' });

      const pdfId = await PDFFile.upload(file);
      const pendingId = await createPendingFileMutation(
        examId,
        [pdfId],
        sampleFile.fileName,
        'application/pdf',
        blob.size,
      )();

      return `${String(pendingId)}:${encodeURIComponent(sampleFile.fileName)}`;
    }),
  );

  return pendingFileNames;
}

export function buildSampleExamMessage(files: SampleExamFile[], language = i18next.language || 'de'): string {
  const key = 'Chat.sampleExamIntro';

  // Normalize language labels (e.g., 'Deutsch') to language codes ('de')
  let resolvedLang = language;
  try {
    if (typeof language === 'string' && language.length > 2) {
      const exact = Object.entries(languageMap).find(([, label]) => label === language);
      const ci = Object.entries(languageMap).find(([, label]) => label.toLowerCase() === language.toLowerCase());
      if (exact) resolvedLang = exact[0];
      else if (ci) resolvedLang = ci[0];
    }
  } catch {
    // fall back to provided language
  }

  const linesArr = files.map(f => {
    const roleKey = ROLE_LABEL_KEYS[f.role];
    const roleLabel = i18next.t(roleKey, { lng: resolvedLang });
    return `- ${roleLabel}: ${f.fileName}`;
  });

  const lines = linesArr.join('\n');
  const defaultValue = `I want to grade this exam with the following documents:\n${lines}`;

  return i18next.t(key, {
    lng: resolvedLang,
    lines,
    defaultValue,
  });
}
